import React, { useState, useEffect, useRef, useContext } from 'react';
import { securePut, api, securePost } from '../config/api';
import { getResponseData } from '../config/api';
import { getJwkKeystore } from '../utils/encryption';
import { useTranslation } from 'react-i18next';
import { SaveCancel } from './PrimaryButton';
import { updateUser } from '../Shared/UserContext/Actions';
import UserContext from '../Shared/UserContext';
import SnackbarNotification from './SnackbarNotification';
import Input from './Input';
import DeleteIcon from '../icons/trashR.svg';

const DynamicForm = ({
  title,
  initialValues = {},
  baseFields = [],
  additionalFields = [],
  fileFields = null,
  onSubmit,
  onSuccess,
  onCancel,
  apiUrl,
  module,
}) => {
  const [formValues, setFormValues] = useState({});
  const [files, setFiles] = useState({});
  const [fileNames, setFileNames] = useState({});
  const [previews, setPreviews] = useState({});
  const inputRef = useRef(null);
  const [, setConditionalBaseFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const { dispatch } = useContext(UserContext);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };


  useEffect(() => {
    const initialFormValues = {};
    const initialConditionalFields = [];

    baseFields.forEach((field) => {
      if (field.type === "checkbox-dropdown") {
        initialFormValues[field.name] = initialValues[field.name] || [];
      } else {
        initialFormValues[field.name] = initialValues[field.name] !== undefined && initialValues[field.name] !== null 
          ? initialValues[field.name] 
          : "";
      }

      if (field.name === "role" && initialValues[field.name] === "ROLE_COMMERCIAL") {
        const relatedFields = field.conditionalFields?.ROLE_COMMERCIAL || [];
        initialConditionalFields.push(...relatedFields);

        relatedFields.forEach((relatedField) => {
          if (relatedField.name === "repositories") {
            initialFormValues[relatedField.name] = initialValues.repositories?.map(
              (repo) => repo.repoId
            ) || [];
          } else {
            initialFormValues[relatedField.name] = initialValues[relatedField.name] !== undefined && initialValues[relatedField.name] !== null 
              ? initialValues[relatedField.name] 
              : "";
          }
        });
      }
    });

    setFormValues(initialFormValues);
    setConditionalBaseFields(initialConditionalFields);
  }, [baseFields, initialValues]);

  useEffect(() => {
    if (initialValues.header) {
      const match = initialValues.header.match(/grayfox\/([^?]+)/);
      if (match) {
        setFileNames((prev) => ({
          ...prev,
          header: match[1],
        }));
      }
    }
    if (initialValues.image) {
      const match = initialValues.image.match(/grayfox\/([^?]+)/);
      if (match) {
        setFileNames((prev) => ({
          ...prev,
          image: match[1],
        }));
      }
    }

  }, [initialValues.image, initialValues.header]);


const handleInputChange = (name, value) => {
  setFormValues(prev => {
    const nextValues = {
      ...prev,
      [name]: value,
    };

    if (fileFields?.length) {
      if (!nextValues.image) {
        nextValues.image = initialValues.image || "";
      }
      if (!nextValues.header) {
        nextValues.header = initialValues.header || "";
      }
    }

    return nextValues;
  });

  const fieldConfig = baseFields.find(field => field.name === name);
  if (fieldConfig?.conditionalFields) {
    const newFields = fieldConfig.conditionalFields[value] || [];
    setConditionalBaseFields(newFields);

    const newConditionalValues = {};
    newFields.forEach(field => {
      newConditionalValues[field.name] =
        field.type === "checkbox-dropdown" ? [] : "";
    });

    setFormValues(prev => ({
      ...prev,
      ...newConditionalValues,
      ...(fileFields?.length
        ? {
            image: prev.image || initialValues.image || "",
            header: prev.header || initialValues.header || "",
          }
        : {}),
    }));
  }
};


  const handleFileChange = (fieldName, file) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreviews(prev => ({ ...prev, [fieldName]: previewUrl }));
      setFiles(prev => ({ ...prev, [fieldName]: file }));
      setFileNames(prev => ({ ...prev, [fieldName]: file.name }));
    }
  };

  const removeFile = (fieldName) => {
    URL.revokeObjectURL(previews[fieldName]);
    setPreviews(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
    setFiles(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
    setFileNames(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  const isModifyMode = initialValues && Object.keys(initialValues).length > 0;
    
  const passwordFieldNames = ['password', 'passwordConfirmation', 'password_confirmation', 'oldPassword', 'newPassword'];

  const requiredFields = [...baseFields];
  const missing = requiredFields.filter((f) => {
    if (isModifyMode && passwordFieldNames.includes(f.name)) {
      return false;
    }
    return f.required && !formValues[f.name];
  });
  
  if (missing.length > 0) {
    showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
    return;
  }

  if (formValues.password && formValues.passwordConfirmation) {
    if (formValues.password !== formValues.passwordConfirmation) {
      showSnackbar(t('placeholder.passwordMismatch'), 'error');
      return;
    }
  }


  const formPayload = { ...formValues };
  const uploadedFileNames = {};

  try {
    setSaving(true);

    // ✅ FILE UPLOADS
    if (fileFields?.length && Object.keys(files).length > 0) {
      const key = await getJwkKeystore();

      for (const [fieldName, file] of Object.entries(files)) {
        const formData = new FormData();
        formData.append('file', file);
        try {
        const fileUploadResponse = await api.post(
          '/attachments/images/add',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: localStorage.getItem('token'),
            },
          }
        );

        const decryptedResponse = await getResponseData(fileUploadResponse, key);
        if (decryptedResponse?.fileName) {
          uploadedFileNames[fieldName] = decryptedResponse.fileName;
        }
        } catch (error) {
          console.error("Error uploading file:", error.response?.data || error);
          showSnackbar(t('errors.generic'), 'error');
          return;
        }
      }
    }

    if (fileFields?.length) {
      fileFields.forEach((field) => {
        const fieldName = field.name;
        const uploaded = uploadedFileNames[fieldName];

        if (uploaded) {
          formPayload[fieldName] = uploaded;
        } else if (formValues[fieldName]) {
          const match = formValues[fieldName].match(/grayfox\/(.+?\.(?:jpg|jpeg|png))/i);
          if (match) {
            formPayload[fieldName] = match[1];
          }
        } else if (initialValues[fieldName]) {
          formPayload[fieldName] = initialValues[fieldName].split('/').pop().split('?')[0];
        } else {
          delete formPayload[fieldName];
        }
      });
    }

    delete formPayload.passwordConfirmation;

    if (formValues.repositories && Array.isArray(formValues.repositories)) {
      const roleField = baseFields.find((field) => field.name === "role");
      const repositoriesField = roleField?.conditionalFields?.ROLE_COMMERCIAL?.find(
        (field) => field.name === "repositories"
      );

      if (repositoriesField?.options) {
        formPayload.repositories = formValues.repositories.map((repoId) => {
          const repo = repositoriesField.options.find((opt) => opt.value === repoId);
          return {
            repoActive: true,
            repoId,
            repoName: repo?.label || "",
          };
        });
      }
    }

    let response;

    if (title === t("form_titles.add_profile")) {
      const profileFieldsChanged = formValues.firstName || formValues.lastName || formValues.preferredLanguage;
      const passwordChanged = formValues.oldPassword && formValues.newPassword;

      if (profileFieldsChanged) {
        const profilePayload = {
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          preferredLanguage: formValues.preferredLanguage,
        };

        response = await securePost('/users/updateProfile', profilePayload, showSnackbar);
        localStorage.setItem('user', JSON.stringify(response.data));
        dispatch(updateUser(response.data));

        if (profilePayload.preferredLanguage) {
          i18n.changeLanguage(profilePayload.preferredLanguage);
          localStorage.setItem('i18nextLng', profilePayload.preferredLanguage);
        }
      }

      if (passwordChanged) {
        response = await securePost('/users/password/change', {
          newPassword: formValues.newPassword,
          oldPassword: formValues.oldPassword,
        }, showSnackbar);
      }

    } else if (title === t("form_titles.add_settings")) {
      if ('header' in formPayload) {
        formPayload.headerImage = formPayload.header;
        delete formPayload.header;
      }

      if ('footer' in formPayload) {
        formPayload.footerText = formPayload.footer;
        delete formPayload.footer;
      }

      if ('language' in formPayload) {
        formPayload.preferredLanguage = formPayload.language;
        delete formPayload.language;
      }

      response = await securePost('/users/updateConfig', formPayload, showSnackbar);
      localStorage.setItem('settings', JSON.stringify(response.data));

    } else {
      response = await securePut(apiUrl, formPayload, showSnackbar);
    }

    onSubmit(response?.data);
    onSuccess();

  } catch (error) {
    console.error("Form submission error:", error);
  } finally {
    setSaving(false);
  }
};

  const handleCancel = () => {
    setFiles(null);
    setFileNames("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onCancel();
  };


  const passwordFields = [
    { label: t(`fields.users.oldPassword`), name: "oldPassword", type: "password", isPasswordConfirmation: true },
    { label: t(`fields.users.newPassword`), name: "newPassword", type: "password" },
    { label: t(`fields.users.passwordConfirmation`), name: "password_confirmation", type: "password", isPasswordConfirmation: true },
  ]

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
      <div className="bg-[#F9FAFC]  px-12 py-8 rounded-[12px] shadow-lg w-full max-w-4xl">
        <h2 className="text-[32px] text-[#38485C] font-semibold mb-6">{title}</h2>
        <form className="space-y-6">
          {/* Base Fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {baseFields
                .filter(field => field.name !== "footer")
                .map((field, index) => (
                  <React.Fragment key={index}>
                    {/* Render Base Field */}
                    <Input
                      type={field.type || "text"}
                      label={field.label}
                      name={field.name}
                      options={field.options || []}
                      value={field.type === "checkbox-dropdown" 
                        ? (formValues[field.name] || [])
                        : (formValues[field.name] !== undefined && formValues[field.name] !== null 
                            ? formValues[field.name] 
                            : "")}
                      required={field.required}
                      onChange={(value) => handleInputChange(field.name, value)}
                      isPasswordConfirmation={field.isPasswordConfirmation}
                    />

                    {field.conditionalFields &&
                      formValues[field.name] in field.conditionalFields &&
                      field.conditionalFields[formValues[field.name]].map((conditionalField, conditionalIndex) => (
                        <Input
                          key={`conditional-${index}-${conditionalIndex}`}
                          type={conditionalField.type || "text"}
                          label={conditionalField.label}
                          name={conditionalField.name}
                          options={conditionalField.options || []}
                          value={conditionalField.type === "checkbox-dropdown" 
                            ? (formValues[conditionalField.name] || [])
                            : (formValues[conditionalField.name] !== undefined && formValues[conditionalField.name] !== null 
                                ? formValues[conditionalField.name] 
                                : "")}
                          required={conditionalField.required}
                          onChange={(value) => handleInputChange(conditionalField.name, value)}
                        />
                      ))}

                  </React.Fragment>
                ))}
            </div>
          </div>

          {/* Password Section */}
          {title === t("form_titles.add_profile") && passwordFields.length > 0 && (
            <div className="space-y-[10px] mt-6">
              <h3 className="text-[16px] font-semibold text-[#ACAFC6]">
                {t("fields_types.changePass")}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {passwordFields.map((field, index) => (
                  <Input
                    key={index}
                    type={field.type || "text"}
                    label={field.label}
                    name={field.name}
                    isPasswordConfirmation={field.isPasswordConfirmation}
                    value={formValues[field.name] || ""}
                    required={field.required}
                    onChange={(value) => handleInputChange(field.name, value)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Additional Fields */}
          {additionalFields.length > 0 && (
            <div className="space-y-[10px]">
              <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.additional`)}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {additionalFields.map((field, index) => (
                  <Input
                    key={index}
                    label={field.label}
                    name={field.name}
                    type={field.type || "text"}
                    value={formValues[field.name] || ""}
                    required={field.required}
                    onChange={(value) => handleInputChange(field.name, value)}
                    options={field.options || []}
                  />
                ))}
              </div>
            </div>
          )}

          {/* File Fields Section */}
          {fileFields && (
            <div className="space-y-[10px]">
              <div className="grid grid-cols-1 gap-4">
                {fileFields.map((field, index) => {
                  const hasFile = Boolean(fileNames[field.name]);
                  const existingImage = initialValues[field.name];
                  const previewImage = previews[field.name];

                  return (
                    <div key={index} className="space-y-[10px]">
                      <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{field.label}</h3>
                      <label
                        htmlFor={`fileInput-${field.name}`}
                        className={` border-2 border-dashed h-[93px] rounded-md p-4 block cursor-pointer transition-colors
                ${hasFile ? 'border-gray-300' : 'border-gray-300'} `}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const droppedFile = e.dataTransfer.files?.[0];
                          if (droppedFile) {
                            const validTypes = field.accept.split(',');
                            const isValid = validTypes.some(type => {
                              if (type === 'image/*') return droppedFile.type.startsWith('image/');
                              return droppedFile.type === type;
                            });

                            if (isValid) {
                              handleFileChange(field.name, droppedFile);
                            } else {
                              setSnackbar(`Invalid file type. Allowed: ${field.accept}`);
                            }
                          }
                        }}
                      >
                        <input
                          id={`fileInput-${field.name}`}
                          type="file"
                          name={field.name}
                          accept={field.accept}
                          className="hidden"
                          onChange={(e) => {
                            const selectedFile = e.target.files?.[0];
                            if (selectedFile) {
                              const validTypes = field.accept.split(',');
                              const isValid = validTypes.some(type => {
                                if (type === 'image/*') return selectedFile.type.startsWith('image/');
                                return selectedFile.type === type;
                              });

                              if (isValid) {
                                handleFileChange(field.name, selectedFile);
                                e.target.value = null;
                              } else {
                                setSnackbar(`Invalid file type. Allowed: ${field.accept}`);
                                e.target.value = null;
                              }
                            }
                          }}
                        />

                        <div className="flex items-center justify-between gap-3 h-full min-h-[40px]">
                          {hasFile ? (
                            <>
                              <div className="flex items-center ml-[40px] min-w-0 flex-1 h-full">
                                {(previewImage || existingImage) && (
                                  <div className="relative w-20 h-20 shrink-1">
                                    <img
                                      src={previewImage || existingImage}
                                      alt="Preview"
                                      className="w-full h-full object-contain rounded-md"
                                    />
                                  </div>
                                )}
                                <span className="text-[14px] font-semibold ml-[20px] text-[#38475D] truncate">
                                  {fileNames[field.name]}
                                </span>
                              </div>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  removeFile(field.name);
                                }}
                                className="w-8 h-8 p-1 -m-1 mr-[36px] flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                              >
                                <img src={DeleteIcon} alt="Delete" className="w-5 h-5" />
                              </button>
                            </>
                          ) : (
                            <div className="flex items-center justify-center w-full gap-4 text-center">
                              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              <div className="text-left">
                                <p className="text-sm text-gray-500">{field.placeholderText1}</p>
                                <p className="text-sm font-bold text-blue-500">{field.placeholderText2}</p>
                              </div>
                            </div>
                          )}
                        </div>

                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer Text Area */}
          {baseFields.some(field => field.name === "footer") && (
            <div className="">
              <div className="col-span-1 space-y-[10px]">
                <label className="text-[16px] font-semibold text-[#ACAFC6] block">
                  {t(`fields.settings.footer`).toUpperCase()}
                </label>
                <textarea
                  className="w-full h-32 p-3 border border-[#E3E3E3] rounded-lg 
              focus:border-[#6A79F8] focus:outline-none hover:border-[#6A79F8] text-[16px] font-medium bg-white text-[#000000]"
                  name="footer"
                  value={formValues.footer || ""}
                  onChange={(e) => handleInputChange("footer", e.target.value)}
                  placeholder={"..."}
                />
              </div>
            </div>
          )}


          {/* Footer Buttons */}
          <div className="flex justify-end space-x-4 mt-6">
            <SaveCancel
              onCancelClick={handleCancel}
              onSaveClick={handleSubmit}
              saving={saving}
            />
          </div>
        </form>
      </div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );

};

export default DynamicForm;
